import { readingRangesForBlock, type ReadingManifest } from "./readingTracker";

const names = { read: "qprins-read", unread: "qprins-unread" };
const storageKey = "qprins-reading-highlights";

export function initializeReadingHighlights(root: Document = document): void {
  const tools = root.querySelector<HTMLElement>("[data-reading-controls]");
  if (!tools || tools.dataset.ready) return;
  tools.dataset.ready = "true";
  const read = tools.querySelector<HTMLInputElement>("[data-highlight-read]")!;
  const unread = tools.querySelector<HTMLInputElement>("[data-highlight-unread]")!;
  const mark = tools.querySelector<HTMLButtonElement>("[data-reading-mark-all]")!;
  const status = tools.querySelector<HTMLElement>("[data-reading-controls-status]")!;
  const remaining = tools.querySelector<HTMLElement>("[data-reading-remaining]")!;
  const registry = globalThis.CSS?.highlights;
  let manifest: ReadingManifest | undefined;
  let busy = false;
  const cached = new Map<string, { element: HTMLElement; text: string; words: Range[] }>();
  try {
    const preference = JSON.parse(localStorage.getItem(storageKey) ?? "{}");
    read.checked = preference.read === true; unread.checked = preference.unread === true;
  } catch { /* Highlighting still works when local storage is unavailable. */ }
  if (!registry || typeof Highlight === "undefined") {
    read.disabled = unread.disabled = true;
    status.textContent = "Text highlighting is unavailable in this browser.";
  }

  function draw(): void {
    const reader = root.querySelector<HTMLElement>("[data-qprins-static-document]");
    if (!manifest || !reader || manifest.documentId !== reader.dataset.readingDocumentId || manifest.revisionId !== reader.dataset.readingRevisionId) return;
    const complete = manifest.progress.percentRead === 100;
    mark.disabled = busy || complete || !manifest.blocks.length;
    const unfinished = manifest.blocks.filter(block => !complete && !block.manuallyRead && block.segments.some(s => s.creditedMilliseconds < s.requiredMilliseconds));
    const milliseconds = unfinished.flatMap(block => block.segments).reduce((n, s) => n + s.requiredMilliseconds - s.creditedMilliseconds, 0);
    remaining.textContent = unfinished.length ? `${unfinished.length} passage${unfinished.length === 1 ? "" : "s"} with incomplete estimates · about ${Math.ceil(milliseconds / 1000)}s remaining` : "All text is marked or estimated as read.";
    if (!registry) return;
    registry.delete(names.read); registry.delete(names.unread);
    if (!read.checked && !unread.checked) return;
    const readRanges: Range[] = [], unreadRanges: Range[] = [];
    function add(words: Range[], target: Range[]): void {
      if (!words.length) return;
      const range = words[0].cloneRange(), last = words.at(-1)!;
      range.setEnd(last.endContainer, last.endOffset); target.push(range);
    }
    for (const block of manifest.blocks) {
      const element = reader.querySelector<HTMLElement>(`#node-${CSS.escape(block.nodeId)}`);
      if (!element) continue;
      let entry = cached.get(block.nodeId);
      if (!entry || entry.element !== element || entry.text !== block.text || entry.words.some(word => !word.startContainer.isConnected || !word.endContainer.isConnected)) {
        const words = readingRangesForBlock(element, block.text);
        if (!words) { cached.delete(block.nodeId); continue; }
        entry = { element, text: block.text, words }; cached.set(block.nodeId, entry);
      }
      let offset = 0;
      for (const segment of block.segments) {
        const words = entry.words.slice(offset, offset + segment.wordCount); offset += segment.wordCount;
        const count = complete || block.manuallyRead ? words.length : Math.floor(words.length * segment.creditedMilliseconds / segment.requiredMilliseconds);
        if (read.checked) add(words.slice(0, count), readRanges);
        if (unread.checked) add(words.slice(count), unreadRanges);
      }
    }
    // CSS highlights preserve native selection, links, annotation offsets and the reader DOM.
    if (read.checked) registry.set(names.read, new Highlight(...readRanges));
    if (unread.checked) registry.set(names.unread, new Highlight(...unreadRanges));
  }

  root.addEventListener("qprins:reading-manifest", event => {
    const incoming = (event as CustomEvent<ReadingManifest>).detail;
    if (manifest?.documentId !== incoming.documentId || manifest.revisionId !== incoming.revisionId) cached.clear();
    manifest = incoming; draw();
  });
  tools.addEventListener("change", () => {
    try { localStorage.setItem(storageKey, JSON.stringify({ read: read.checked, unread: unread.checked })); } catch { /* Session-only preferences. */ }
    draw();
  });
  root.addEventListener("qprins:reading-mark-status", event => {
    const detail = (event as CustomEvent<{ state: string; message?: string }>).detail;
    busy = detail.state === "saving";
    status.textContent = busy ? "Saving…" : detail.state === "saved" ? "Marked as read. The time-based estimate is preserved separately." : detail.message ?? "Could not save. Try again.";
    draw();
  });
  root.addEventListener("pointerdown", event => {
    if (event.target instanceof Node && !tools.contains(event.target)) tools.removeAttribute("open");
  });
  root.addEventListener("keydown", event => {
    if (event.key === "Escape" && tools.hasAttribute("open")) { tools.removeAttribute("open"); tools.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true }); }
  });
  const clear = () => { registry?.delete(names.read); registry?.delete(names.unread); };
  root.defaultView?.addEventListener("beforeprint", clear);
  root.defaultView?.addEventListener("afterprint", draw);
  root.defaultView?.addEventListener("pagehide", clear);
  root.defaultView?.addEventListener("pageshow", draw);
}
